import React, { useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Typography } from "antd";
import { SbSpin } from "uiCore";
import ProgressCircleCardV2 from "Components/ProgressCircleCard/ProgressCircleCardV2";
import {
  PageContainerStyled,
  TableWrapperStyled,
} from "styles/common.styles";
import PageHeader from "Components/PageHeader";
import { PageTransitionStyled } from "./talentMobility.animations";
import { ScaleFooterStyled, ScaleHeadingStyled, ScaleItemsRowStyled, ScaleItemTextStyled } from "./SkillGapDetailV2.styles";
import SkillGapList from "./components/SkillGapList";
import LearningCatalystLink from "./components/LearningCatalystLink";
import { IndexInfoLabel } from "./components/IndexTooltip";
import { useSkillGapForPair, useSavedCareerPaths } from "./useTalentMobilityData";
import { TEXT, PROFICIENCY_SCALE } from "./talentMobility.constants";
import useAuthStore from "Store/useAuthStore";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { CROSS_PAGE_TOUR_PHASE, SKILL_GAP_TOUR_STEPS } from "./TalentMobilityEmployeeV2.constants";
import { decodeToken } from "Components/SbSessionCheckerComponent/SbSessionChecker";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

// Deliberately mirrors EmployeeSkillsBridgerV2.jsx's own layout (header +
// ProgressCircleCardV2 summary + skill rows) — req #15 requires this page
// to look and behave identically to SkillsBridger's own skill-gap view, not
// just "similar."
//
// :opportunityId is the position's JD sb_unique_id (from the career-paths
// position cards). The header renders the clicked card's own data, passed
// via navigation state from the parent /talent-mobility-employee page
// (falling back, for deep links, to the caller's saved POST /career-paths
// response fetched back from the backend); skills come from
// GET /talent-mobility/employee-skills (user_id = signed-in user from the
// JWT, current_jd_id = this JD), fetched by useSkillGapForPair with a
// spinner until it resolves.
const ANALYTICS_FROM = "talent_mobility_skill_gap_detail";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.OPPORTUNITY_VIEWED;

const SkillGapDetailV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const parentRole = navState?.role || null;

  // Deep links (a fresh load straight onto /opportunity/:id) arrive with no
  // navigation state — fetch the caller's saved career paths back from the
  // backend so the header still gets the position's display fields. When
  // the parent page's card provided them, skip the call. Failures stay
  // silent here: /employee-skills' own job_title/profile_score remain the
  // fallback.
  const { saved: careerPaths, isLoading: careerPathsLoading } = useSavedCareerPaths(!parentRole);

  const accessToken = useAuthStore((state) => state.accessToken);
  const currentUserId = useMemo(() => {
    const claims = decodeToken(accessToken);
    return claims?.sb_user_unique_id || claims?.sub || null;
  }, [accessToken]);

  // The same JD can appear on several tracks with identical display fields —
  // first hit wins.
  const position = useMemo(() => {
    const tracks = careerPaths?.tracks || [];
    for (const track of tracks) {
      const match = (track.positions || []).find((p) => p.jd_sb_unique_id === opportunityId);
      if (match) return match;
    }
    return null;
  }, [careerPaths, opportunityId]);

  const { skillGap, roleTitle, matchPct, isLoading } = useSkillGapForPair(opportunityId, currentUserId);

  // ── Guided tour: Skill Gap leg (handed off from the pathways view) ─────────
  // Next returns to the pathways view, which resumes at its Apply step;
  // dismissing returns to its Finish step instead.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const skillGapListRef = useRef(null);
  const returnToPathwaysTour = (phase) => {
    setCrossPageTourPhase(phase);
    navigate("/talent-mobility-employee");
  };

  // Header precedence: what the parent page's card showed (navigation
  // state) → the saved career-paths position (deep link within the
  // session) → the /employee-skills response's own job_title/profile_score.
  const title = parentRole?.title || position?.title || roleTitle;
  const metaLine = [
    parentRole?.function ?? position?.department,
    parentRole?.location ?? position?.location,
    parentRole?.level ?? position?.level,
  ]
    .filter(Boolean)
    .join(" · ");
  const smiScore = Math.round(navState?.matchPct ?? position?.smi_score ?? matchPct ?? 0);
  // Same precedence for the develop count: the clicked card's value shows
  // immediately (career-paths sends skills_to_develop, null today); until
  // the backend populates it, it's computed from the fetched gap rows.
  const skillsToDevelop =
    navState?.skillsToDevelop ??
    position?.skills_to_develop ??
    (isLoading ? null : skillGap.filter((skill) => skill.current < skill.required).length);

  // careerPathsLoading gates deep links: /employee-skills can settle (404s
  // resolve to empty data with isLoading false) while the career-paths
  // lookup that supplies the header is still in flight — without the gate
  // this branch flashes over a page that is about to render fine.
  if (!isLoading && !careerPathsLoading && !parentRole && !position && !title && !skillGap.length) {
    return (
      <PageContainerStyled>
        <PageHeader title="Opportunity not found" />
      </PageContainerStyled>
    );
  }

  return (
    <PageTransitionStyled>
      <PageHeader
        title={title || "—"}
        subtitle={metaLine}
        onBack={() => navigate("/talent-mobility-employee")}
        backLabel={TEXT.BACK_TO_PATHWAYS}
        middle={
          <ProgressCircleCardV2 score={smiScore} title={<IndexInfoLabel index="SMI">Your SMI Score</IndexInfoLabel>} subtitle="(Required vs Current Proficiency)" />
        }
        actions={
          <>
            {skillsToDevelop != null && <Text type="secondary">{skillsToDevelop} skill(s) to develop</Text>}
            <LearningCatalystLink />
          </>
        }
      />

      <TableWrapperStyled>
        <div ref={skillGapListRef} style={{ padding: "16px 24px" }}>
          {isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <SbSpin size="large" />
            </div>
          ) : (
            <SkillGapList skillGap={skillGap} showScore={false} />
          )}
        </div>
      </TableWrapperStyled>

      <ScaleFooterStyled>
        <ScaleHeadingStyled>{TEXT.SCALE_HEADING}</ScaleHeadingStyled>
        <ScaleItemsRowStyled>
          {PROFICIENCY_SCALE.map((field) => (
            <ScaleItemTextStyled key={field.level}>
              {field.level}{TEXT.SCALE_SEPARATOR}<strong>{field.name}</strong>
            </ScaleItemTextStyled>
          ))}
        </ScaleItemsRowStyled>
      </ScaleFooterStyled>

      {/* Skill Gap leg of the Talent Mobility (Employee) tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.SKILL_GAP}
        ready={!isLoading}
        steps={SKILL_GAP_TOUR_STEPS}
        resolveTarget={() => skillGapListRef.current}
        onNext={() => returnToPathwaysTour(CROSS_PAGE_TOUR_PHASE.APPLY)}
        onClose={() => returnToPathwaysTour(CROSS_PAGE_TOUR_PHASE.FINISH)}
      />
    </PageTransitionStyled>
  );
};

export default SkillGapDetailV2;
